// 育成状態（Profile）と資源（Resources）。Profile は JSON でそのまま保存できる。
// 資源はサーバーの user_resources で別管理。meta の操作関数は (profile, res) を直接書き換えて結果を返す。
import type { BuildingType, CharId, ItemId } from '../types';
import { CHAR_IDS } from '../types';
import { CONFIG } from './config';
import { newSkillState, type CharSkills } from './skills';

export interface Resources {
  zeni: number;
  reidama: number;
  zaimoku: number;
  /** 主EXPの累計（主Lvはここから levelInfo で求める） */
  exp: number;
  /** meta では使わない（兵糧の実体は Profile.stamina）。サーバーの列との互換のために残す */
  hyoro: number;
  /** 式神の札 */
  fuda: number;
  /** ガチャ券（1枚で1回。内部キーは kuji。霊玉とは別。円では買えない） */
  kuji: number;
  /** 掃討チケット（1枚で掃討1戦。毎日配る。兵糧とは別。円では買えない） */
  soutou: number;
}
export type ResourceKey = keyof Resources;
export type Bundle = Partial<Resources>;

export const newResources = (): Resources => ({ zeni: 0, reidama: 0, zaimoku: 0, exp: 0, hyoro: 0, fuda: 0, kuji: 0, soutou: 0 });

/** 並・良・優・極。極は章ボスの上の難易度（difficulty≥1）でのみ出る */
export type Rarity = 'n' | 'r' | 'sr' | 'ssr';
export const RARITIES: readonly Rarity[] = ['n', 'r', 'sr', 'ssr'];
export type EquipSlot = 'weapon' | 'armor' | 'netsuke';
export const EQUIP_SLOTS: readonly EquipSlot[] = ['weapon', 'armor', 'netsuke'];

export type AffixId =
  | 'atk'
  | 'hp'
  | 'def'
  | 'agi'
  | 'core'
  | 'boss'
  | 'neutral'
  | 'skillPower'
  | 'buildHp'
  | 'buildPower'
  | 'lifesteal'
  | 'matStart'
  | 'potionHeal'
  | 'matIncome'
  | 'respawn'
  // 追加（2026-10-20）：陣地・守り・倹約・範囲・反撃・再生・吉（ハード以上）
  | 'territory'
  | 'guard'
  | 'splash'
  | 'counter'
  | 'regen'
  | 'luck'
  // デメリット付きの強い銘（ハード以上）
  | 'sutemi'
  | 'donju'
  | 'jutou'
  | 'kyouhon'
  | 'kesshi'
  // 賽3つ（rules 2）：賽の合計・役で効く
  | 'chou'
  | 'han'
  | 'oome'
  | 'kome'
  | 'soroi'
  | 'yakusha'
  | 'saijutsu';
/** 追加効果（銘）。value は表示どおりの大きさ（% は 0.05 = 5%）、q は幅の中での品質 0..1 */
export interface Affix {
  id: AffixId;
  value: number;
  q: number;
}
/** 旧保存データの銘（読み込み時に affixes へ移行する） */
export interface MeiRoll {
  id: AffixId;
  value: number;
}
export type AutoSell = 'none' | 'n' | 'r';

export interface EquipItem {
  id: number;
  defId: string;
  slot: EquipSlot;
  /** 武器だけキャラ専用。防具・根付は null */
  charId: CharId | null;
  tier: number; // 段（章）1..10
  rarity: Rarity;
  level: number; // 強化Lv
  lb: number; // 限界突破
  /** 追加効果（並0〜1・良1〜2・優2〜3・極3〜4、重複なし） */
  affixes: Affix[];
  /** 旧保存データのみ（normalizeProfile で affixes に移す） */
  mei?: MeiRoll | null;
  rerolls: number; // 今日の振り直し回数
  rerollDay: string;
  /** ロック中は売却・限界突破の素材・一括売却の対象外 */
  locked?: boolean;
  /** 業物のシリーズ（equipment.ts の EQUIP_SETS。同じシリーズを2部位・3部位そろえると効果。ハード以上で出る） */
  set?: string;
  /** 神品（極の上位。銘が4〜5つ。ハード以上で出る） */
  shin?: boolean;
}

export interface EquipSet {
  weapon: number | null;
  armor: number | null;
  netsuke: number | null;
}

/** お守りの副効果（GAME_DESIGN 18章）。lv は 1..5（surge は Lv なし＝1） */
export type OmamoriSubId = 'atk' | 'hp' | 'def' | 'agi' | 'skillPower' | 'buildHp' | 'buildPower' | 'core' | 'neutral' | 'surge';
export interface OmamoriSub {
  id: OmamoriSubId;
  lv: number;
}
/** キャラごとのお守りの枠の数（GAME_DESIGN 18章。2026-10-21 に3 → 1：ルール変化のお守りを重ねられないように） */
export const OMAMORI_SLOTS = 1;
/** おまもりガチャの抽選ランクの内部キー（互換のため旧名）。順に S・A・B・C・D */
export type Fortune = 'daikichi' | 'chukichi' | 'kichi' | 'suekichi' | 'kyo';

/** お守り（ランダム生成の個体。omamori.ts） */
export interface OmamoriItem {
  id: number;
  /** 主効果（OMAMORI のキー。外伝品は見た目違いで、効果は sameAs と同じ） */
  defId: string;
  /** 並・良・優・極（副効果の数と Lv の上限が上がる） */
  rarity: Rarity;
  /** 主効果の品質 0..1（効果の大きさ＝幅の中の位置） */
  q: number;
  /** 副効果（0〜3個、重複なし） */
  subs: OmamoriSub[];
  /** ロック中は錬成・売却の素材にできない */
  locked?: boolean;
  /** 旧課金品（完成品として引き継いだもの）。売却・錬成できない。返金で消える */
  paid?: boolean;
  /** 旧データ（固定12種）から変換した個体 */
  legacy?: boolean;
  /** 旧保存データのみ（normalizeOmamori で q に移す） */
  stage?: number;
}

export interface ShikigamiState {
  star: number; // 0 = 未所持（撃破数だけ）。エリート・章ボスの30体だけ記録する。★＝限界突破の段階（スキルの解放と式神Lvの上限）
  kills: number;
  /** 式神Lv（1〜50、上限は★×10。霊玉で上げる）。無い旧データは読み込み時に1（normalizeProfile） */
  level?: number;
}

export interface StageProgress {
  stars: [boolean, boolean, boolean];
  clears: number;
}

export type FacilityId = 'kura' | 'hokora' | 'seizaisho' | 'hyorogura' | 'dojo' | 'kajiya' | 'yashiro';
export const FACILITY_IDS: readonly FacilityId[] = ['kura', 'hokora', 'seizaisho', 'hyorogura', 'dojo', 'kajiya', 'yashiro'];

export interface StaminaState {
  value: number;
  reserve: number;
  /** value/reserve を最後に確定させた時刻（ms） */
  at: number;
  /** 月パスの速い回復で、その日（JST）に自然回復した量（1日の上限は無料と同じ。stamina.ts） */
  day?: string;
  got?: number;
}

/** キャラごとの持ち物：連れていく式神（null＝なし）と持ち出す道具 */
export interface CharKit {
  companion: string | null;
  items: ItemId[];
}

export interface Profile {
  v: 1;
  nextId: number;
  /** 装備の強化の目盛り（2＝+100まで・1段1%。無ければ旧目盛り（+20まで・1段5%）で、normalizeProfile が換算する） */
  equipV?: number;
  /** 主Lv（Resources.exp から同期した値。兵糧の最大値などに使う） */
  level: number;
  /** キャラごとのスキル構成（skills.ts。auto ならおすすめ構成に任せる） */
  skills: Record<CharId, CharSkills>;
  /** クリアした外伝（解放力の上乗せ） */
  gaidenCleared: CharId[];
  items: EquipItem[];
  sets: Record<CharId, EquipSet>;
  omamori: OmamoriItem[];
  /** キャラごとのお守りの枠（OMAMORI_SLOTS＝1つの配列。null＝空き）。1つの個体は1キャラだけ（旧データの3枠・共通の omamoriSlots は normalizeOmamori が戦力の最も高い1つだけ残す） */
  omamoriSets: Record<CharId, (number | null)[]>;
  shikigami: Record<string, ShikigamiState>;
  /** 出撃キャラのお供の写し（正は kits のキャラごと。旧データでは全キャラ共通の設定。kits に無いキャラの既定にもなる） */
  companion: string | null;
  /** 出撃キャラの持ち出しアイテムの写し（正は kits のキャラごと。旧データでは全キャラ共通） */
  battleItems?: ItemId[];
  /** キャラごとの式神（お供）と持ち出す道具。ビルドがキャラごとに違うので別々に持つ（古いデータは migrate で共通の設定を写す） */
  kits?: Partial<Record<CharId, CharKit>>;
  /** チュートリアル（案内役 賽丸）で見終えた案内のID。古いデータには無い（何も見ていない） */
  tutorial?: string[];
  /** 旧データの移行：式神でなくなった小型・中型の★の数（settleLegacyShikigami が銭で補填して消す） */
  legacyShikiStars?: number;
  /** 旧データの移行：式神の作り直し（10種族）で消えた式神に使っていた霊玉（settleLegacyShikiLevels が返して消す） */
  legacyShikiReidama?: number;
  /** 旧データの移行：式神Lv の導入前に上げていた★の数（★-1 の合計。settleLegacyShikigami が霊玉で補填して消す） */
  legacyShikiLvStars?: number;
  blueprints: Partial<Record<BuildingType, number>>;
  village: { levels: Record<FacilityId, number>; lastCollect: number };
  stamina: StaminaState;
  /** キー：`${stageId}` は通常、`${stageId}:${difficulty}` は上の難易度 */
  progress: Record<string, StageProgress>;
  dex: string[];
  oshi: CharId;
  /** 月パスの期限（ms）。買うたびに30日延びる（meta/monthly.ts） */
  monthlyUntil?: number;
  /** 課金の永続権利：'exp2x' 'auto' 'speed4' 'gaiden:akari' 'skin:...' 'omamori:<defId>' など */
  entitlements: string[];
  /** reset は旧スキルツリーのリセット券（廃止。読み込み時に補填して消す）。chat はお問い合わせのチャットチケット（meta/support.ts） */
  tickets: { rename: number; reset?: number; fuse?: number; chat?: number };
  /** お問い合わせ：使った無料の回数（通算。Google 連携で freeLinked 回まで。meta/support.ts） */
  supportFree?: number;
  /** お問い合わせのチャットチケット：購入ごとの枚数と期限（ms）。meta/support.ts */
  chatLots?: { n: number; until: number }[];
  slotPacks: number;
  /** EXP倍率（EXP2倍・寄進）の上乗せを除いた累計EXP。Lv100以降の霊玉換算はこの増分だけを使う */
  freeExp: number;
  overflow: { day: string; reidama: number };
  /** pity は極のお守りが出ていない回数（札・無料の合計）。pulls は札で引いた累計 */
  gacha: { pity: number; pulls: number };
  /** おまもりガチャ（保存キーは互換のため omikuji）：無料の1回を引いた日・その日のボーナス（ランク）・D の翌日の S・A アップの日・錬成の再送用の記録 */
  omikuji: { freeDay: string; perk: { day: string; fortune: Fortune } | null; luckDay: string; free: number };
  /** 御朱印（廃止。交換所と一緒に 2026-10-31 に消した。保存データの互換のため残す） */
  goshuin: number;
  /** 交換所の今週の交換回数（廃止。互換のため残す） */
  exchange: { week: string; bought: Record<string, number> };
  /** 錬成の一度きり（nonce → 作ったお守りの id。新しい20件） */
  fuseLog: { nonce: string; id: number }[];
  login: { days: number; last: string };
  daily: { day: string; sorties: number; missions: Record<string, number>; claimed: string[] };
  weekly: { week: string; missions: Record<string, number>; claimed: string[] };
  /** 万屋：その日（day）に品物を買った回数 */
  shop: { day: string; count: Record<string, number> };
  /** 万屋で解放した道具（回復薬は最初から持っているので入れない） */
  ownedItems?: ItemId[];
  hounou: number;
  /**
   * オート周回（GAME_DESIGN 20章）：周回中の戦場とキャラ、最後に1戦を終えた（または始めた）時刻。active のまま閉じたら、
   * 次に開いたとき lastAt から farm.graceMinutes 分をサーバーが計算する（POST /api/farm/settle）。止めたら active=false
   */
  /** pending：閉じたあとの周回をサーバーが先に計算して取ってある（通知を送った人。次の farm/settle で渡す） */
  farm?: { stageId: number; difficulty: number; charId: CharId; lastAt: number; active: boolean; aliveAt?: number; pending?: boolean };
  /** 設定。autoSell：拾った装備のうちこのレアリティ以下をその場で売る */
  /** avatar：主人公の姿（player-*）。旧データの図鑑の妖 id も表示可能。無ければ人影 */
  /** autoSellTier：自動売却は段がこれ以下のものだけ（無ければ段は問わない） */
  /** frame：チャットのアイコン縁（月パスの期間中だけ表示。data/chat-frames.ts） */
  /** hideFeats＝自分の実績（式神が仲間に・国の制圧）を全体チャットに流さない */
  settings: { autoSell: AutoSell; autoSellTier?: number; avatar?: string; frame?: string; hideFeats?: boolean };
}

export const stageKey = (stageId: number, difficulty = 0) => (difficulty > 0 ? `${stageId}:${difficulty}` : `${stageId}`);
export const chapterOf = (stageId: number) => Math.ceil(stageId / 10);
/** ゲストで出撃できる戦場か（第1章まで。2章からは Google 連携が要る） */
export const guestStageOpen = (stageId: number) => chapterOf(stageId) <= CONFIG.guest.maxChapter;
export const indexInChapter = (stageId: number) => ((stageId - 1) % 10) + 1;
export const isChapterBoss = (stageId: number) => stageId % 10 === 0;

export function newProfile(now = 0, oshi: CharId = 'akari'): Profile {
  const p: Profile = {
    v: 1,
    equipV: 2,
    nextId: 1,
    level: 1,
    skills: newSkillState(),
    gaidenCleared: [],
    items: [],
    sets: Object.fromEntries(CHAR_IDS.map((c) => [c, { weapon: null, armor: null, netsuke: null }])) as Record<CharId, EquipSet>,
    omamori: [],
    omamoriSets: Object.fromEntries(CHAR_IDS.map((c) => [c, Array<number | null>(OMAMORI_SLOTS).fill(null)])) as Record<CharId, (number | null)[]>,
    shikigami: {},
    companion: null,
    battleItems: ['heal'],
    blueprints: { yagura: 0 },
    village: { levels: { kura: 0, hokora: 0, seizaisho: 0, hyorogura: 0, dojo: 0, kajiya: 0, yashiro: 0 }, lastCollect: now },
    stamina: { value: CONFIG.stamina.baseMax + 1, reserve: 0, at: now },
    progress: {},
    dex: [],
    oshi,
    entitlements: [],
    tickets: { rename: 0 },
    slotPacks: 0,
    freeExp: 0,
    overflow: { day: '', reidama: 0 },
    gacha: { pity: 0, pulls: 0 },
    omikuji: { freeDay: '', perk: null, luckDay: '', free: 0 },
    goshuin: 0,
    exchange: { week: '', bought: {} },
    fuseLog: [],
    login: { days: 0, last: '' },
    daily: { day: '', sorties: 0, missions: {}, claimed: [] },
    weekly: { week: '', missions: {}, claimed: [] },
    shop: { day: '', count: {} },
    ownedItems: [],
    hounou: 0,
    settings: { autoSell: 'none' },
  };
  // 初期装備：各キャラの1段目の武器
  for (const c of CHAR_IDS) {
    const id = p.nextId++;
    p.items.push({ id, defId: `w_${c}_1`, slot: 'weapon', charId: c, tier: 1, rarity: 'n', level: 0, lb: 0, affixes: [], rerolls: 0, rerollDay: '' });
    p.sets[c].weapon = id;
  }
  return p;
}

export function hasEntitlement(p: Profile, key: string) {
  return p.entitlements.includes(key);
}

/** クリア済みの最高ステージID（通常難易度で1回以上クリア）。国盗りでは順番に進まないので「最も強いクリア済みの戦場」の意味（装備・ガチャの段に使う） */
export function highestCleared(p: Profile): number {
  let best = 0;
  for (const [k, v] of Object.entries(p.progress)) {
    if (k.includes(':') || v.clears <= 0) continue;
    best = Math.max(best, Number(k));
  }
  return best;
}

export function totalStars(p: Profile, difficulty = 0): number {
  let n = 0;
  for (const [k, v] of Object.entries(p.progress)) {
    const d = k.includes(':') ? Number(k.split(':')[1]) : 0;
    if (d === difficulty) n += v.stars.filter(Boolean).length;
  }
  return n;
}

// isStageUnlocked は国盗り（provinces.ts）に移した

export function addBundle(res: Resources, b: Bundle, sign = 1) {
  for (const k of Object.keys(b) as ResourceKey[]) res[k] = (res[k] ?? 0) + sign * (b[k] ?? 0);
}

export type OpResult<T = {}> = ({ ok: true } & T) | { ok: false; error: string };

/** チュートリアルの案内を見終えた印を付ける（ids は英小文字・数字・記号 -_:. の40文字まで、全体で200件まで）。reset で全部消す */
export const TUTORIAL_MAX = 200;
export function markTutorial(p: Profile, ids: unknown, reset = false): OpResult<{ tutorial: string[] }> {
  if (reset) {
    p.tutorial = [];
    return { ok: true, tutorial: [] };
  }
  if (!Array.isArray(ids) || ids.length < 1 || ids.length > 20) return { ok: false, error: 'body' };
  const cur = new Set(p.tutorial ?? []);
  for (const id of ids) {
    if (typeof id !== 'string' || !/^[a-z0-9_.:-]{1,40}$/.test(id)) return { ok: false, error: 'id' };
    cur.add(id);
  }
  p.tutorial = [...cur].slice(-TUTORIAL_MAX);
  return { ok: true, tutorial: p.tutorial };
}
